import {
  EMPTY_NOTEBOOK,
  type LessonNotebookState,
  NOTEBOOK_LIMITS,
  NotebookAnalysisSchema,
  boardPhrases,
  mergeAnalysis,
  missedBoardKeys,
  withBoardReview,
} from "@/lib/lessonNotebook";
import type { LiveKitRoomSessionProof } from "@/lib/livekit-room-participant-token";
import { createLogger } from "@/lib/logger";
import type { WhiteboardSessionBoard } from "@/lib/whiteboard/boardResults";
import { useEffect, useMemo, useRef, useState } from "react";

export type { LessonNotebookState, NotebookPhrase } from "@/lib/lessonNotebook";

const logger = createLogger("lesson-notebook");

/** Wait for the tutor to finish before analysing new turns. */
const ANALYSIS_DEBOUNCE_MS = 2000;

/**
 * Session notebook from the tutor's finished turns and the board's results.
 * Learner speech is never read. New tutor turns and board phrases are analysed
 * debounced, one request at a time, and only while a notebook is shown; a wrong
 * board tap flags the matching phrase at once. Notes use the instruction language.
 */
export function useLessonNotebook({
  tutorTurns,
  boards,
  targetLanguage,
  languageLevel,
  noteLanguage,
  enabled,
  session,
}: {
  /** Finished tutor turns, oldest first. */
  tutorTurns: string[];
  boards: WhiteboardSessionBoard[];
  targetLanguage: string;
  languageLevel: string;
  noteLanguage?: string;
  enabled: boolean;
  session?: LiveKitRoomSessionProof | null;
}) {
  const [notebook, setNotebook] = useState<LessonNotebookState>(EMPTY_NOTEBOOK);
  const [analyzing, setAnalyzing] = useState(false);
  // Bumped when a request ends, so turns that arrived meanwhile are analysed next.
  const [settled, setSettled] = useState(0);
  const analyzed = useRef({ turns: 0, phrases: "" });
  const retryAt = useRef(0);
  const inFlight = useRef(false);
  const latest = useRef({ tutorTurns, boards });
  latest.current = { tutorTurns, boards };
  const phrasesKey = boardPhrases(boards).join("\n");
  const missed = useMemo(() => missedBoardKeys(boards), [boards]);
  const shown = useMemo(() => withBoardReview(notebook, missed), [notebook, missed]);

  useEffect(() => {
    if (!enabled || !session) return;
    const fresh =
      tutorTurns.length > analyzed.current.turns || phrasesKey !== analyzed.current.phrases;
    if (!fresh || (!tutorTurns.length && !phrasesKey)) return;
    const timer = setTimeout(
      async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        const turns = latest.current.tutorTurns;
        const phrases = boardPhrases(latest.current.boards);
        const previous = analyzed.current;
        analyzed.current = { turns: turns.length, phrases: phrases.join("\n") };
        setAnalyzing(true);
        try {
          const response = await fetch("/api/widgets/lesson-notebook", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.capability}`,
              "X-ChickyTutor-Room": session.roomName,
              "X-ChickyTutor-Participant": session.participantIdentity,
            },
            body: JSON.stringify({
              tutorTurns: turns
                .slice(-NOTEBOOK_LIMITS.tutorTurns)
                .map((turn) => turn.slice(0, NOTEBOOK_LIMITS.turnChars)),
              boardPhrases: phrases,
              targetLanguage,
              languageLevel,
              noteLanguage,
            }),
          });
          if (!response.ok) {
            if (response.status === 429) {
              // Keep this snapshot queued, respecting the shared provider budget
              // even when new tutor turns arrive during the wait.
              const delay = Number(response.headers.get("Retry-After"));
              retryAt.current =
                Date.now() +
                (Number.isFinite(delay) && delay > 0 ? Math.min(60, Math.ceil(delay)) : 60) * 1000;
              analyzed.current = previous;
            }
            logger.warn("Notebook analysis unavailable", { status: response.status });
            return;
          }
          const analysis = NotebookAnalysisSchema.safeParse(await response.json());
          if (!analysis.success) {
            logger.warn("Notebook analysis malformed");
            return;
          }
          setNotebook((previous) => mergeAnalysis(previous, analysis.data, Date.now()));
        } catch (error) {
          logger.warn("Notebook analysis unavailable", {
            error: error instanceof Error ? error.message : String(error),
          });
        } finally {
          inFlight.current = false;
          setAnalyzing(false);
          setSettled((value) => value + 1);
        }
      },
      Math.max(ANALYSIS_DEBOUNCE_MS, retryAt.current - Date.now())
    );
    return () => clearTimeout(timer);
  }, [
    enabled,
    tutorTurns.length,
    phrasesKey,
    targetLanguage,
    languageLevel,
    noteLanguage,
    settled,
    session,
  ]);

  return { ...shown, analyzing };
}
